import { type ProjectCode } from '@constants'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { HStack } from '@pets-ui/HStack/HStack'
import { Image } from '@pets-ui/Image/Image'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import * as stylex from '@stylexjs/stylex'
import { useHistory } from 'react-router-dom-v5'
import { brandImageMap } from '../../constants/brand-image-map'
import { useStmBrandStore } from '../../hooks'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const SKELETON_COUNT = 8

const styles = stylex.create({
  outerWrapper: {
    'scrollbarWidth': 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  innerTrack: {
    margin: '0 auto',
    gap: { [MOBILE]: 12, default: 20 },
  },
  logoButton: {
    borderRadius: { [MOBILE]: 18, default: 36 },
    overflow: 'hidden',
    height: { [MOBILE]: 60, default: 120 },
    width: { [MOBILE]: 60, default: 120 },
  },
})

interface BrandLogoStripProps {
  campaigns: CrossBrandMallHomeCampaign[]
  isLoading: boolean
  basePath: string
  onBrandClick?: (projectCode: ProjectCode) => void
}

export const BrandLogoStrip = ({ campaigns, isLoading, basePath, onBrandClick }: BrandLogoStripProps) => {
  const { brands, setBrand } = useStmBrandStore()
  const history = useHistory()
  const authorizedBrandCodes = new Set(brands.map(brand => brand.campaignCode))

  if (isLoading) {
    return (
      <HStack overflow="auto" width="100%" rootStyle={styles.outerWrapper}>
        <HStack flexShrink={0} rootStyle={styles.innerTrack}>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <Skeleton key={`brand-logo-skeleton-${index}`} rootStyle={styles.logoButton} />
          ))}
        </HStack>
      </HStack>
    )
  }

  const brandLogoEntries = [...campaigns]
    .sort((a, b) => a.display_order - b.display_order)
    .flatMap(campaign => {
      if (!authorizedBrandCodes.has(campaign.code)) return []
      const customLogoUrl = campaign.logos[0]?.url
      const logoUrl = customLogoUrl || brandImageMap[campaign.project_code]?.icon
      return logoUrl ? [{ campaign, logoUrl }] : []
    })

  if (brandLogoEntries.length === 0) return null

  const handleNavigateToBrand = (campaign: CrossBrandMallHomeCampaign) => {
    onBrandClick?.(campaign.project_code)
    setBrand(campaign.code)
    history.push(`${basePath}/product-catalog`)
  }

  return (
    <HStack overflow="auto" width="100%" rootStyle={styles.outerWrapper}>
      <HStack flexShrink={0} rootStyle={styles.innerTrack}>
        {brandLogoEntries.map(({ campaign, logoUrl }) => (
          <ButtonBase
            key={campaign.campaign_id}
            onClick={() => handleNavigateToBrand(campaign)}
            rootStyle={styles.logoButton}
          >
            <Image src={logoUrl} alt={campaign.name} width="100%" height="100%" objectFit="cover" />
          </ButtonBase>
        ))}
      </HStack>
    </HStack>
  )
}
